import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Cloud as CloudIcon,
  AddCircleOutline as InvestigationIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { Investigation } from '../../../types';
import InvestigationScopeModal from './InvestigationScopeModal';
import { useCreateInvestigationForm } from '../hooks/useCreateInvestigationForm';

interface CreateInvestigationModalProps {
  isOpen: boolean;
  customerId: string;
  onClose: () => void;
  onInvestigationCreated: (investigation: Investigation) => void;
}

// Scope only. The agent is the investigation agent a Prompt Studio admin set, and a playbook is
// chosen from the case itself, for as long as it is open: choosing one here meant choosing
// before anyone knew what the case was, in the dialog operators skip.
const CreateInvestigationModal = memo<CreateInvestigationModalProps>(({
  isOpen,
  customerId,
  onClose,
  onInvestigationCreated,
}) => {
  const form = useCreateInvestigationForm({ isOpen, customerId, onClose, onInvestigationCreated });

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={form.handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InvestigationIcon color="primary" />
              <Typography variant="h6">Create New Investigation</Typography>
            </Box>
            <IconButton onClick={form.handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={form.handleSubmit}>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              <TextField
                label="AWS Accounts (Optional)"
                value={form.scope.length === 0 ? 'None selected' : `${form.getTotalAccountsInScope()} account${form.getTotalAccountsInScope() !== 1 ? 's' : ''} selected`}
                onClick={() => form.setShowScopeModal(true)}
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CloudIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ cursor: 'pointer' }}
                helperText="Select AWS accounts to investigate (optional - you can also use knowledge bases only)"
              />

              {form.error && (
                <InlineAlert severity="error">{form.error}</InlineAlert>
              )}
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={form.handleClose} disabled={form.loading} variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={form.loading}
              >
                {form.loading ? 'Creating...' : 'Create Investigation'}
              </Button>
            </Box>
          </DialogActions>
        </form>
      </Dialog>

      <InvestigationScopeModal
        isOpen={form.showScopeModal}
        customerId={customerId}
        title="Select Investigation Scope"
        onSave={form.handleScopeSelected}
        onCancel={() => form.setShowScopeModal(false)}
        initialScope={form.scope}
      />
    </>
  );
});

CreateInvestigationModal.displayName = 'CreateInvestigationModal';

export default CreateInvestigationModal;
